import React, { useState } from 'react';
import { Link } from 'react-router-dom';

// Guarded so a bundler context where `process` is undefined can't crash the page.
// The `typeof` check short-circuits before `process.env` is ever dereferenced;
// CRA's build still replaces the inner `process.env.PUBLIC_URL` with a literal.
const PUBLIC_URL = (typeof process !== 'undefined' && process.env) ? process.env.PUBLIC_URL : '';

const AiServices = () => {
    // activeCap = which capability tab is currently open (0 = the first one).
    // setActiveCap updates it when a tab is clicked, which re-renders the panel.
    const [activeCap, setActiveCap] = useState(0);

    // Each array below feeds one section of the page. To edit the page copy,
    // change the text here rather than hunting through the markup down below.

    // How AI & Automation improves a client's business — outcome-first, not feature-first
    const outcomes = [
        {
            icon: '⏱️',
            title: 'Reclaim Staff Hours',
            description: 'Automate the repetitive work, data entry, routing, reporting, follow-ups, so your team spends its time on the work that actually needs a person.'
        },
        {
            icon: '📉',
            title: 'Cut Operating Costs',
            description: 'Do more without adding headcount. Automated pipelines run around the clock at a fraction of the cost of manual processing.'
        },
        {
            icon: '🎯',
            title: 'Reduce Costly Errors',
            description: 'Consistent, rule-driven processing with human-in-the-loop checks removes the manual mistakes that create rework and risk.'
        },
        {
            icon: '⚡',
            title: 'Decide & Move Faster',
            description: 'Surface the answer buried in your data instantly, so decisions that used to take days happen in minutes.'
        }
    ];

    // What we actually build
    const capabilities = [
        {
            icon: '🧠',
            iconBg: 'rgba(2, 132, 199, 0.1)',
            iconColor: '#0284c7',
            title: 'Generative AI & LLMs',
            description: 'Private, domain-specific language models that answer questions from your own data with citations, not guesses. Deployed securely inside your environment.',
            features: ['Private LLM Deployment', 'Retrieval-Augmented Generation (RAG)', 'Chatbots & Virtual Assistants']
        },
        {
            icon: '🔄',
            iconBg: 'rgba(14, 165, 233, 0.1)',
            iconColor: '#0ea5e9',
            title: 'Intelligent Workflows',
            description: 'Decision pipelines that route tasks, classify documents, and extract data automatically, connecting the tools your team already uses.',
            features: ['Document Processing', 'Smart Routing Agents', 'Human-in-the-loop Systems']
        },
        {
            icon: '⚙️',
            iconBg: 'rgba(15, 23, 42, 0.1)',
            iconColor: '#0f172a',
            title: 'Process Automation',
            description: 'End-to-end automation of the repetitive tasks that eat your team\'s week, from data sync to reporting, with the reliability production work demands.',
            features: ['RPA Integration', 'Data Extraction & Sync', 'Automated Reporting']
        },
        {
            icon: '🎯',
            iconBg: 'rgba(124, 58, 237, 0.1)',
            iconColor: '#7c3aed',
            title: 'Prompt & Loop Engineering',
            description: 'The tuning that makes AI dependable in production, so agents complete complex, multi-step tasks the same way every time, with guardrails you can trust.',
            features: ['Prompt Design & Optimization', 'Agentic Loop Architecture', 'Output Evaluation & Guardrails']
        }
    ];

    // Where it drives results — by function
    const useCases = [
        {
            icon: '🎧',
            title: 'Customer Support',
            description: 'AI assistants that resolve common questions instantly and hand off cleanly to a person for the rest, cutting response times without cutting quality.'
        },
        {
            icon: '📄',
            title: 'Operations & Back Office',
            description: 'Invoices, forms, and records processed and routed automatically, clearing the backlog that slows everything else down.'
        },
        {
            icon: '🛡️',
            title: 'Compliance & Risk',
            description: 'Automated document review and audit trails that flag what needs attention and keep a defensible record of every decision.'
        },
        {
            icon: '📊',
            title: 'Finance & Reporting',
            description: 'Data pulled, reconciled, and turned into the report you need on schedule, without the manual spreadsheet marathon.'
        }
    ];

    // How an engagement runs
    const process = [
        {
            step: '01',
            title: 'Assess',
            description: 'We map your workflows and find the highest-value automation opportunities, the ones that pay for themselves fastest.'
        },
        {
            step: '02',
            title: 'Pilot',
            description: 'A focused proof-of-concept on one real process, so you see measurable results before committing to scale.'
        },
        {
            step: '03',
            title: 'Deploy',
            description: 'We build it into your environment, secure, tested, and integrated with the tools your team already uses.'
        },
        {
            step: '04',
            title: 'Scale',
            description: 'We expand what works across teams and processes, with ongoing tuning as your needs grow.'
        }
    ];

    return (
        <div className="page-wrapper">
            {/* Hero Section */}
            <section className="hero" style={{ minHeight: '64vh', backgroundImage: `linear-gradient(rgba(8,15,30,0.72) 0%, rgba(8,15,30,0.45) 100%), url(${PUBLIC_URL}/images/dark-dev-night.jpg)`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
                <div className="hero-content hero-centered">
                    <span className="hero-badge">⚡ Our Flagship Practice</span>
                    <h1>
                        Put <span className="text-gradient">AI to Work</span> Inside Your Business
                    </h1>
                    <p>
                        We don't just build AI, we use it to remove the bottlenecks slowing your
                        organization down. Secure, production-grade automation that cuts costs,
                        saves hours, and helps your team do their best work.
                    </p>
                    <div className="hero-buttons">
                        <Link to="/contact" className="btn btn-primary">
                            Get a Free AI Assessment →
                        </Link>
                    </div>
                </div>
            </section>

            {/* The Business Case — how it improves other businesses */}
            <section className="section">
                <div className="container">
                    <div className="section-header">
                        <div className="section-label">Why It Matters</div>
                        <h2>What AI Does for <span className="text-gradient">Your Business</span></h2>
                        <p>
                            The goal is never technology for its own sake. It's a measurable
                            difference in how your organization runs, day to day.
                        </p>
                    </div>
                    <div className="services-grid grid-even">
                        {outcomes.map((item, index) => (
                            <div key={index} className="card card-gradient service-card">
                                <div className="service-icon">{item.icon}</div>
                                <h3>{item.title}</h3>
                                <p>{item.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Impact Stats */}
            <section className="section section-navy">
                <div className="container">
                    <div className="section-header">
                        <h2 style={{ color: 'white' }}>The <span className="text-gradient">Impact</span></h2>
                        <p style={{ color: 'rgba(255,255,255,0.6)' }}>
                            What well-targeted automation typically delivers. We'll size the numbers
                            to your operation during the assessment.
                        </p>
                    </div>
                    <div className="stats-grid">
                        <div className="stat-item">
                            <h3>60%+</h3>
                            <p style={{ color: 'rgba(255,255,255,0.55)' }}>Less time on manual, repetitive processing</p>
                        </div>
                        <div className="stat-item">
                            <h3>24/7</h3>
                            <p style={{ color: 'rgba(255,255,255,0.55)' }}>Automated operations that never clock out</p>
                        </div>
                        <div className="stat-item">
                            <h3>1000s</h3>
                            <p style={{ color: 'rgba(255,255,255,0.55)' }}>Of staff hours reclaimed per year</p>
                        </div>
                        <div className="stat-item">
                            <h3>SOC2</h3>
                            <p style={{ color: 'rgba(255,255,255,0.55)' }}>Compliant, government-ready deployments</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Core Capabilities — interactive tabbed showcase.
                Left rail = clickable tabs (one per capability).
                Right panel = the detail for whichever tab is active. */}
            <section className="section">
                <div className="container">
                    <div className="section-header">
                        <div className="section-label">What We Build</div>
                        <h2>Capabilities That <span className="text-gradient">Deliver</span></h2>
                        <p>
                            From private language models to full agentic systems, engineered to
                            integrate with the infrastructure you already run.
                        </p>
                    </div>
                    <div className="cap-showcase">
                        {/* The tabs: loop over each capability and render a clickable button.
                            Clicking one calls setActiveCap(index) to open it. The active
                            tab gets an extra "active" class so the CSS can highlight it. */}
                        <div className="cap-tabs" role="tablist" aria-label="AI capabilities">
                            {capabilities.map((cap, index) => (
                                <button
                                    key={index}
                                    type="button"
                                    role="tab"
                                    aria-selected={activeCap === index}
                                    className={`cap-tab${activeCap === index ? ' active' : ''}`}
                                    onClick={() => setActiveCap(index)}
                                >
                                    <span className="cap-tab-icon" style={{ background: cap.iconBg, color: cap.iconColor }}>{cap.icon}</span>
                                    <span className="cap-tab-title">{cap.title}</span>
                                    <span className="cap-tab-arrow">→</span>
                                </button>
                            ))}
                        </div>
                        {/* The panel: shows details for the active capability only.
                            capabilities[activeCap] is whichever tab is currently open.
                            key={activeCap} makes React re-mount it on each switch, which
                            replays the little fade-in animation defined in the CSS. */}
                        <div className="cap-panel" role="tabpanel" key={activeCap}>
                            <div className="cap-panel-icon" style={{ background: capabilities[activeCap].iconBg, color: capabilities[activeCap].iconColor }}>
                                {capabilities[activeCap].icon}
                            </div>
                            <h3>{capabilities[activeCap].title}</h3>
                            <p>{capabilities[activeCap].description}</p>
                            <ul className="cap-feature-list">
                                {capabilities[activeCap].features.map((f, fi) => (
                                    <li key={fi}><span className="cap-check">✓</span>{f}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* Use Cases — where it drives results */}
            <section className="section bg-dark">
                <div className="container">
                    <div className="section-header">
                        <div className="section-label">Where It Fits</div>
                        <h2>Results Across <span className="text-gradient">Every Team</span></h2>
                        <p>
                            Automation isn't one-size-fits-all. Here's where it tends to pay off first.
                        </p>
                    </div>
                    <div className="services-grid grid-even">
                        {useCases.map((uc, index) => (
                            <div key={index} className="card card-gradient service-card">
                                <div className="service-icon">{uc.icon}</div>
                                <h3>{uc.title}</h3>
                                <p>{uc.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How We Deliver — process */}
            <section className="section">
                <div className="container">
                    <div className="section-header">
                        <div className="section-label">How We Work</div>
                        <h2>From Idea to <span className="text-gradient">Impact</span></h2>
                        <p>A proven path that shows measurable value before you commit to scale.</p>
                    </div>
                    <div className="services-grid grid-even">
                        {process.map((item, index) => (
                            <div key={index} className="card card-gradient" style={{ textAlign: 'left' }}>
                                <span className="text-gradient" style={{ fontSize: '2.5rem', fontWeight: '800' }}>
                                    {item.step}
                                </span>
                                <h3 style={{ margin: '1rem 0 0.5rem' }}>{item.title}</h3>
                                <p className="text-muted">{item.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="section bg-dark">
                <div className="container">
                    <div className="split-layout">
                        <div className="split-content">
                            <h2>Ready to See What's Possible?</h2>
                            <p className="mb-2xl">
                                Start with a free assessment. We'll map your workflows, pinpoint the
                                automation that pays for itself fastest, and show you the numbers,
                                no commitment required.
                            </p>
                            <Link to="/contact" className="btn btn-primary">
                                Book Your AI Assessment →
                            </Link>
                        </div>
                        <div className="split-image">
                            <div className="card card-gradient" style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <div style={{ fontSize: '4rem' }}>🤖</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default AiServices;
